/**
 * In-app chimes when a new unread CRM bell arrives.
 * Web Audio only — no binary assets. Browsers need a user gesture first.
 */

export type BellSoundId =
  | 'desk'
  | 'glass'
  | 'knock'
  | 'marimba'
  | 'chime'
  | 'ping'
  | 'drop'
  | 'piano'
  | 'pop'
  | 'tone'
  | 'ding'
  | 'harp';

export interface BellSoundOption {
  id: BellSoundId;
  label: string;
  hint: string;
}

export const DEFAULT_BELL_SOUND_ID: BellSoundId = 'desk';

export const BELL_SOUND_OPTIONS: BellSoundOption[] = [
  { id: 'desk', label: 'Desk bell', hint: 'Two-note chime' },
  { id: 'glass', label: 'Glass ping', hint: 'Bright crystal tap' },
  { id: 'knock', label: 'Knock', hint: 'Two wood knocks' },
  { id: 'marimba', label: 'Marimba', hint: 'Warm pentatonic' },
  { id: 'chime', label: 'Chime', hint: 'Three-note wind chime' },
  { id: 'ping', label: 'Ping', hint: 'Short high ping' },
  { id: 'drop', label: 'Drop', hint: 'Soft water drop' },
  { id: 'piano', label: 'Piano', hint: 'Quiet two-note' },
  { id: 'pop', label: 'Pop', hint: 'Message bubble' },
  { id: 'tone', label: 'Tone', hint: 'Two-tone alert' },
  { id: 'ding', label: 'Ding', hint: 'Single desk ding' },
  { id: 'harp', label: 'Harp', hint: 'Short arpeggio' },
];

const SOUND_IDS = new Set<string>(BELL_SOUND_OPTIONS.map((o) => o.id));

export function resolveBellSoundId(id: unknown): BellSoundId {
  if (typeof id === 'string' && SOUND_IDS.has(id)) return id as BellSoundId;
  return DEFAULT_BELL_SOUND_ID;
}

let audioCtx: AudioContext | null = null;
let noiseBuf: AudioBuffer | null = null;
let lastPlayAt = 0;
const MIN_GAP_MS = 1400;

function getAudioContext(): AudioContext | null {
  const AC = window.AudioContext || (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AC) return null;
  if (!audioCtx) audioCtx = new AC();
  return audioCtx;
}

function noiseBuffer(ctx: AudioContext): AudioBuffer {
  if (noiseBuf && noiseBuf.sampleRate === ctx.sampleRate) return noiseBuf;
  const n = ctx.sampleRate * 0.6;
  const buf = ctx.createBuffer(1, n, ctx.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < n; i++) data[i] = Math.random() * 2 - 1;
  noiseBuf = buf;
  return buf;
}

export function unlockBellSound(): void {
  const ctx = getAudioContext();
  if (ctx && ctx.state === 'suspended') {
    void ctx.resume();
  }
}

export function installBellSoundUnlock(): () => void {
  const onGesture = () => unlockBellSound();
  document.addEventListener('pointerdown', onGesture, { passive: true });
  document.addEventListener('keydown', onGesture, { passive: true });
  return () => {
    document.removeEventListener('pointerdown', onGesture);
    document.removeEventListener('keydown', onGesture);
  };
}

/** Missing / undefined pref means on. */
export function isBellSoundEnabled(prefs: { bellSound?: boolean } | null | undefined): boolean {
  return prefs?.bellSound !== false;
}

export function shouldPlayBellSound(
  previousIds: Set<string> | null,
  nextIds: Iterable<string>,
): { play: boolean; next: Set<string> } {
  const next = new Set(nextIds);
  if (previousIds == null) return { play: false, next };
  const ids = Array.from(next);
  for (let i = 0; i < ids.length; i++) {
    if (!previousIds.has(ids[i])) return { play: true, next };
  }
  return { play: false, next };
}

function envGain(ctx: AudioContext, dest: AudioNode, at: number, peak: number, hold: number, duration: number): GainNode {
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, at);
  g.gain.exponentialRampToValueAtTime(peak, at + 0.008);
  g.gain.setValueAtTime(peak, at + hold);
  g.gain.exponentialRampToValueAtTime(0.0001, at + duration);
  g.connect(dest);
  return g;
}

function sine(
  ctx: AudioContext,
  dest: AudioNode,
  freq: number,
  at: number,
  peak: number,
  duration: number,
  type: OscillatorType = 'sine',
): void {
  const osc = ctx.createOscillator();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, at);
  osc.connect(envGain(ctx, dest, at, peak, 0.04, duration));
  osc.start(at);
  osc.stop(at + duration);
}

function sweep(
  ctx: AudioContext,
  dest: AudioNode,
  from: number,
  to: number,
  at: number,
  peak: number,
  duration: number,
  type: OscillatorType = 'sine',
): void {
  const osc = ctx.createOscillator();
  osc.type = type;
  osc.frequency.setValueAtTime(from, at);
  osc.frequency.exponentialRampToValueAtTime(Math.max(20, to), at + duration);
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, at);
  g.gain.exponentialRampToValueAtTime(peak, at + 0.012);
  g.gain.exponentialRampToValueAtTime(0.0001, at + duration);
  osc.connect(g);
  g.connect(dest);
  osc.start(at);
  osc.stop(at + duration);
}

function noiseBurst(
  ctx: AudioContext,
  dest: AudioNode,
  at: number,
  peak: number,
  duration: number,
  freq: number,
  q = 4,
): void {
  const src = ctx.createBufferSource();
  src.buffer = noiseBuffer(ctx);
  const filter = ctx.createBiquadFilter();
  filter.type = 'bandpass';
  filter.frequency.setValueAtTime(freq, at);
  filter.Q.value = q;
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, at);
  g.gain.exponentialRampToValueAtTime(peak, at + 0.006);
  g.gain.exponentialRampToValueAtTime(0.0001, at + duration);
  src.connect(filter);
  filter.connect(g);
  g.connect(dest);
  src.start(at);
  src.stop(at + duration);
}

function strikeBell(ctx: AudioContext, dest: AudioNode, freq: number, at: number, peak: number, duration: number): void {
  sine(ctx, dest, freq, at, peak, duration);
  sine(ctx, dest, freq * 2.76, at, peak * 0.2, duration * 0.45);
}

function playVoice(ctx: AudioContext, id: BellSoundId): void {
  const t = ctx.currentTime;
  const master = ctx.createGain();
  master.gain.value = 1;
  master.connect(ctx.destination);

  switch (id) {
    case 'glass':
      sine(ctx, master, 2093, t, 0.42, 0.35);
      sine(ctx, master, 3136, t, 0.16, 0.18);
      break;
    case 'knock':
      noiseBurst(ctx, master, t, 0.55, 0.09, 380, 2.2);
      sine(ctx, master, 140, t, 0.28, 0.12);
      noiseBurst(ctx, master, t + 0.13, 0.5, 0.09, 340, 2.2);
      sine(ctx, master, 125, t + 0.13, 0.24, 0.12);
      break;
    case 'marimba':
      sine(ctx, master, 523.25, t, 0.42, 0.38, 'triangle');
      sine(ctx, master, 659.25, t + 0.14, 0.36, 0.42, 'triangle');
      break;
    case 'chime':
      strikeBell(ctx, master, 1046.5, t, 0.32, 0.42);
      strikeBell(ctx, master, 1318.5, t + 0.11, 0.3, 0.46);
      strikeBell(ctx, master, 1568, t + 0.22, 0.26, 0.52);
      break;
    case 'ping':
      sine(ctx, master, 1760, t, 0.38, 0.16);
      sine(ctx, master, 3520, t, 0.1, 0.08);
      break;
    case 'drop':
      sweep(ctx, master, 980, 420, t, 0.36, 0.18);
      sine(ctx, master, 420, t + 0.12, 0.12, 0.16);
      break;
    case 'piano':
      sine(ctx, master, 523.25, t, 0.34, 0.46, 'triangle');
      sine(ctx, master, 659.25, t + 0.16, 0.3, 0.5, 'triangle');
      sine(ctx, master, 1046.5, t, 0.08, 0.22);
      break;
    case 'pop':
      noiseBurst(ctx, master, t, 0.28, 0.05, 1800, 1.1);
      sine(ctx, master, 620, t, 0.28, 0.1);
      sine(ctx, master, 1240, t, 0.1, 0.07);
      break;
    case 'tone':
      sine(ctx, master, 880, t, 0.34, 0.16);
      sine(ctx, master, 698.46, t + 0.14, 0.32, 0.22);
      break;
    case 'ding':
      strikeBell(ctx, master, 1046.5, t, 0.44, 0.48);
      sine(ctx, master, 1568, t, 0.12, 0.22);
      break;
    case 'harp':
      sine(ctx, master, 523.25, t, 0.28, 0.32, 'triangle');
      sine(ctx, master, 659.25, t + 0.07, 0.26, 0.34, 'triangle');
      sine(ctx, master, 783.99, t + 0.14, 0.24, 0.38, 'triangle');
      sine(ctx, master, 1046.5, t + 0.21, 0.22, 0.44, 'triangle');
      break;
    case 'desk':
    default:
      strikeBell(ctx, master, 880, t, 0.48, 0.32);
      strikeBell(ctx, master, 1318.5, t + 0.12, 0.4, 0.38);
      break;
  }
}

export function playBellNotificationSound(opts?: { force?: boolean; id?: unknown }): void {
  const nowMs = Date.now();
  if (!opts?.force && nowMs - lastPlayAt < MIN_GAP_MS) return;
  const ctx = getAudioContext();
  if (!ctx) return;
  lastPlayAt = nowMs;
  const id = resolveBellSoundId(opts?.id);

  const start = () => playVoice(ctx, id);
  if (ctx.state === 'suspended') {
    void ctx.resume().then(start).catch(() => undefined);
    return;
  }
  start();
}
